import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faLock,
  faUser,
  faHeadset,
  faExclamationTriangle,
  faInfoCircle,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  Col,
  Row,
  Form,
  Card,
  Button,
  Container,
  InputGroup,
  Modal,
} from "@themesberg/react-bootstrap";
import { Link, useNavigate } from "react-router-dom";

import { Routes } from "../../routes";

// Replace this with your actual logo asset
// import BrandLogo from "../../assets/img/brand/brandlogo.jpg";
import BrandLogo from "../../assets/img/brand/Brand_Logo.png";
import { loginSendOtp, loginVerifyOtp } from "../api";

// const SEND_OTP_API_URL = "/auth/send-otp";
// const VERIFY_OTP_API_URL = "/auth/verify-otp";

const otpRegex = /^\d{6}$/;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const getPasswordLoginPath = () => {
  return Routes?.Signin?.path || "/sign-in";
};

const getSupportPath = () => {
  return Routes?.Support?.path || "#";
};

const getRedirectPathByRole = (role) => {
  const normalizedRole = String(role || "").trim().toUpperCase();

  switch (normalizedRole) {
    case "SUPER_ADMIN":
    case "ADMIN":
    case "ROLE_ADMIN":
    case "ROLE_SUPER_ADMIN":
      return Routes?.AdminDashboard?.path || "/admin-dashboard";

    case "USER":
    case "ROLE_USER":
    case "CHILD":
    case "ROLE_CHILD":
      return Routes?.UserDashboard?.path || "/user-dashboard";
    default:
      return Routes?.UserDashboard?.path || "/user-dashboard";
  }
};

const maskEmail = (email) => {
  const value = String(email || "").trim();
  const atIndex = value.indexOf("@");

  if (atIndex <= 1) return value;

  const name = value.slice(0, atIndex);
  const domain = value.slice(atIndex);

  if (name.length <= 2) {
    return `${name[0]}***${domain}`;
  }

  return `${name[0]}${"*".repeat(Math.max(name.length - 2, 3))}${name[name.length - 1]}${domain}`;
};

const storeSessionAuthData = (responseData) => {
  const token = responseData?.token;
  const tokenType = responseData?.token_type || "Bearer";
  const userData = responseData?.data || {};
  const role = userData?.user_type || responseData?.role || responseData?.userRole || "";
  const profilePhoto = userData?.profile_photo || responseData?.profile_photo;

  if (token) {
    sessionStorage.setItem("authToken", token);
    sessionStorage.setItem("authTokenType", tokenType);
    sessionStorage.setItem("authUser", JSON.stringify(userData));
    sessionStorage.setItem("authRole", role);
    sessionStorage.setItem("isAuthenticated", "true");
    sessionStorage.setItem("authProfilePhoto", profilePhoto);
  }

  return getRedirectPathByRole(role);
};

export default function LoginWithOtp() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    loginId: "",
    otp: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [activeModal, setActiveModal] = useState(null);
  const [modalMessage, setModalMessage] = useState("");
  const [supportModalOpen, setSupportModalOpen] = useState(false);

  const [otpSent, setOtpSent] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [otpSessionId, setOtpSessionId] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);

  const trimmedLoginId = useMemo(() => formData.loginId.trim(), [formData.loginId]);
  const trimmedOtp = useMemo(() => formData.otp.trim(), [formData.otp]);

  const canVerifyOtp = otpSent && otpRegex.test(trimmedOtp) && !submitting && !otpVerified;
  const canResendOtp = otpSent && cooldownSeconds === 0 && !submitting;

  useEffect(() => {
    if (cooldownSeconds <= 0) return;

    const timer = setInterval(() => {
      setCooldownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldownSeconds]);

  const openModal = (type, message = "") => {
    setActiveModal(type);
    setModalMessage(message);
  };

  const closeModal = () => {
    setActiveModal(null);
    setModalMessage("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFieldErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateLoginId = () => {
    if (!trimmedLoginId) {
      setFieldErrors((prev) => ({
        ...prev,
        loginId: "Registered Email ID is required.",
      }));
      return false;
    }

    if (!emailRegex.test(trimmedLoginId)) {
      setFieldErrors((prev) => ({
        ...prev,
        loginId: "Invalid email address.",
      }));
      return false;
    }

    return true;
  };

  const sendOtp = async () => {
    if (!validateLoginId()) return;

    try {
      setSubmitting(true);

      // const response = await axios.post(SEND_OTP_API_URL, {
      //   email: trimmedLoginId
      // });

      const response = await loginSendOtp({
        email: trimmedLoginId,
        type: "login",
      });

      const data = response?.data || {};

      if (data?.success === false) {
        openModal("technicalError", data?.message || "Unable to send OTP. Please try again.");
        return;
      }

      if (data?.status === "NOT_FOUND") {
        openModal("accountNotFound", "No account matched the entered registered email ID.");
        return;
      }

      if (data?.status === "INACTIVE") {
        openModal("inactive", "Your account exists but is inactive.");
        return;
      }

      if (data?.status === "BLOCKED") {
        openModal("blocked", "Your account exists but is blocked.");
        return;
      }

      setOtpSent(true);
      setOtpVerified(false);
      setMaskedEmail(data?.maskedEmail || maskEmail(trimmedLoginId));
      setOtpSessionId(data?.sessionId || "");
      setCooldownSeconds(Number(data?.cooldownSeconds || 30));
      setFormData((prev) => ({
        ...prev,
        otp: "",
      }));

      openModal(
        "otpSent",
        `OTP sent successfully to ${data?.maskedEmail || maskEmail(trimmedLoginId)}.`
      );
    } catch (error) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message || "Unable to send OTP. Please try again.";

      if (status === 404) {
        openModal("accountNotFound", message);
      } else if (status === 423) {
        openModal("inactive", message);
      } else if (status === 403) {
        openModal("blocked", message);
      } else if (status === 429) {
        openModal("tooManyRequests", message);
      } else {
        openModal("technicalError", message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const verifyAndLogin = async () => {
    const errors = {};

    if (!otpRegex.test(trimmedOtp)) {
      errors.otp = "Enter a valid 6-digit OTP.";
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      setSubmitting(true);

      const response = await loginVerifyOtp({
        email: trimmedLoginId,
        otp: trimmedOtp,
        // type: "login",
        // sessionId: otpSessionId,
      });

      const data = response?.data || {};

      if (data?.success === false) {
        openModal("invalidOtp", data?.message || "Entered OTP does not match.");
        return;
      }

      if (data?.status === "INVALID_OTP") {
        openModal("invalidOtp", "Entered OTP does not match.");
        return;
      }

      if (data?.status === "OTP_EXPIRED") {
        openModal("otpExpired", "OTP validity period expired.");
        return;
      }

      if (data?.status === "TOO_MANY_ATTEMPTS") {
        openModal("tooManyAttempts", "Too many invalid OTP verification attempts.");
        return;
      }

      const redirectPath = storeSessionAuthData(data);

      setOtpVerified(true);
      navigate(redirectPath, { replace: true });
    } catch (error) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message || "OTP verification failed. Please try again.";

      if (status === 401) {
        openModal("invalidOtp", message);
      } else if (status === 410) {
        openModal("otpExpired", message);
      } else if (status === 429) {
        openModal("tooManyAttempts", message);
      } else if (status === 404) {
        openModal("accountNotFound", message);
      } else if (status === 423) {
        openModal("inactive", message);
      } else if (status === 403) {
        openModal("blocked", message);
      } else {
        openModal("technicalError", message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resendOtp = async () => {
    if (!canResendOtp) return;

    try {
      setSubmitting(true);

      const response = await loginSendOtp({
        email: trimmedLoginId,
        type: "login",
        // sessionId: otpSessionId,
      });

      const data = response?.data || {};

      setMaskedEmail(data?.maskedEmail || maskedEmail || maskEmail(trimmedLoginId));
      setOtpSessionId(data?.sessionId || otpSessionId);
      setCooldownSeconds(Number(data?.cooldownSeconds || 30));
      setFormData((prev) => ({
        ...prev,
        otp: "",
      }));

      openModal(
        "otpResent",
        `New OTP has been sent to ${data?.maskedEmail || maskedEmail || maskEmail(trimmedLoginId)}.`
      );
    } catch (error) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message || "Unable to resend OTP. Please try again.";

      if (status === 429) {
        openModal("tooManyRequests", message);
      } else {
        openModal("technicalError", message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleOtpChange = (e) => {
    const nextValue = e.target.value.replace(/\D/g, "").slice(0, 6);
    setFormData((prev) => ({
      ...prev,
      otp: nextValue,
    }));
    setFieldErrors((prev) => ({
      ...prev,
      otp: "",
    }));
  };

  return (
    <main className="auth-shell">
      <section className="py-5">
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8} xl={6}>
              <Card className="border-0 auth-card">
                <Card.Body className="p-4 p-md-5">
                  <div className="auth-logo-wrap">
                    <img src={BrandLogo} alt="LifeSkill Global" />
                  </div>

                  <div className="text-center mb-4">
                    <h4 className="fw-bold mb-0">Login via OTP</h4>
                  </div>

                  <Form noValidate>
                    <Form.Group className="mb-3" controlId="loginId">
                      <Form.Label className="fw-semibold">Registered Email ID</Form.Label>
                      <InputGroup
                        className={`flex-nowrap ${fieldErrors.loginId ? "invalid-input-group" : ""}`}
                      >
                        <InputGroup.Text className="bg-white">
                          <FontAwesomeIcon icon={faEnvelope} />
                        </InputGroup.Text>
                        <Form.Control
                          name="loginId"
                          type="email"
                          placeholder="Enter Registered Email ID"
                          value={formData.loginId}
                          onChange={handleChange}
                          isInvalid={!!fieldErrors.loginId}
                          className="py-2 border-start-0"
                          style={{ boxShadow: "none" }}
                          autoComplete="email"
                        />
                        <Button
                          type="button"
                          onClick={sendOtp}
                          disabled={submitting}
                          style={{
                            minWidth: "120px",
                            ...ACTIVE_BUTTON_STYLE,
                          }}
                        >
                          Send OTP
                        </Button>
                      </InputGroup>
                      <Form.Control.Feedback type="invalid" className="d-block">
                        {fieldErrors.loginId}
                      </Form.Control.Feedback>
                    </Form.Group>

                    {otpSent && (
                      <>
                        {/* <Form.Group className="mb-3" controlId="maskedEmail">
                          <Form.Label className="fw-semibold">Registered Email</Form.Label>
                          <Form.Control
                            type="text"
                            value={maskedEmail}
                            readOnly
                            className="py-2"
                            placeholder="Masked email will appear here"
                          />
                        </Form.Group> */}

                        <Form.Group className="mb-3" controlId="otp">
                          <Form.Label className="fw-semibold">OTP</Form.Label>
                          <InputGroup
                            className={`flex-nowrap ${fieldErrors.otp ? "invalid-input-group" : ""}`}
                          >
                            <InputGroup.Text className="bg-white">
                              <FontAwesomeIcon icon={faLock} />
                            </InputGroup.Text>
                            <Form.Control
                              name="otp"
                              type="text"
                              inputMode="numeric"
                              maxLength={6}
                              placeholder="Enter 6-digit OTP"
                              value={formData.otp}
                              onChange={handleOtpChange}
                              isInvalid={!!fieldErrors.otp}
                              className="py-2 border-start-0 border-end-0"
                              style={{ boxShadow: "none" }}
                            />
                            <InputGroup.Text
                              className="p-0"
                              style={{
                                background: "#e9ecef",
                                minWidth: "110px",
                                justifyContent: "center",
                                borderColor: "#dee2e6",
                              }}
                            >
                              <Button
                                type="button"
                                variant="link"
                                onClick={resendOtp}
                                disabled={!canResendOtp}
                                className="w-100 h-100 p-0 text-decoration-none border-0 shadow-none d-flex align-items-center justify-content-center"
                                style={{
                                  background: "transparent",
                                  color: "#5f6b7a",
                                  minHeight: "46px",
                                  fontSize: "0.9rem",
                                }}
                              >
                                {cooldownSeconds > 0 ? `Resend (${cooldownSeconds})` : "Resend OTP"}
                              </Button>
                            </InputGroup.Text>
                          </InputGroup>
                          <Form.Control.Feedback type="invalid" className="d-block">
                            {fieldErrors.otp}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </>
                    )}

                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                      <Link
                        to={getPasswordLoginPath()}
                        className="fw-semibold underline-link"
                        style={{ color: "#1D4E89" }}
                      >
                        Back to Password Login
                      </Link>

                      {otpSent ? (
                        <span className="small text-muted">
                          OTP sent to {maskedEmail || "your registered email"}
                        </span>
                      ) : null}
                    </div>

                    <div className="d-flex justify-content-center">
                      <Button
                        type="button"
                        disabled={!canVerifyOtp}
                        onClick={verifyAndLogin}
                        className="px-5 py-2 fw-bold"
                        style={{
                          minWidth: "160px",
                          ...ACTIVE_BUTTON_STYLE,
                        }}
                      >
                        {submitting ? "VERIFYING..." : "VERIFY & LOGIN"}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="justify-content-center mt-4 mt-md-5">
            <Col xs={12} md={10} lg={8} xl={6}>
              <Card className="border-0 shadow-sm rounded-4" style={{ background: "#ffffff" }}>
                <Card.Body className="p-4 p-md-5">
                  <h4 className="fw-bold mb-2">Need help?</h4>
                  <p className="mb-0 text-muted">
                    Contact your administrator if you can't access your account.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      <Modal show={activeModal === "otpSent"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" />
            OTP Sent Successfully
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMessage || "OTP sent successfully to your registered email."}
        </Modal.Body>
        <Modal.Footer>
          <Button className="act-btn" onClick={closeModal}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "accountNotFound"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger" />
            Account Not Found
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMessage || "No account matched the entered registered email ID."}
        </Modal.Body>
        <Modal.Footer>
          <Button className="act-btn" onClick={closeModal}>
            Try Again
          </Button>
          {/* <Button className="act-btn" onClick={() => setSupportModalOpen(true)}>
            Contact Support
          </Button> */}
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "inactive"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-warning" />
            Inactive Account
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage || "Account exists but is inactive."}</Modal.Body>
        <Modal.Footer>
          <Button onClick={closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "blocked"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger" />
            Blocked Account
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage || "Account exists but is blocked."}</Modal.Body>
        <Modal.Footer>
          <Button onClick={closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "invalidOtp"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger" />
            Invalid OTP
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage || "Entered OTP does not match."}</Modal.Body>
        <Modal.Footer>
          <Button onClick={closeModal}>
            Retry
          </Button>
          <Button className="act-btn" onClick={resendOtp}>
            Resend OTP
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "otpExpired"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-warning" />
            OTP Expired
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage || "OTP validity period expired."}</Modal.Body>
        <Modal.Footer>
          <Button onClick={closeModal}>
            Close
          </Button>
          <Button className="act-btn" onClick={resendOtp}>
            Resend OTP
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "otpResent"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" />
            OTP Resent Successfully
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage || "New OTP issued successfully."}</Modal.Body>
        <Modal.Footer>
          <Button className="act-btn" onClick={closeModal}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "tooManyAttempts"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
            Too Many OTP Attempts
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMessage || "Too many invalid OTP verification attempts."}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "tooManyRequests"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
            Too Many OTP Requests
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMessage || "Too many resend requests. Please wait and try again."}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "technicalError"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger" />
            Technical Error
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage || "Send/verify OTP failed technically."}</Modal.Body>
        <Modal.Footer>
          <Button className="act-btn" onClick={closeModal}>
            Retry
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={supportModalOpen} onHide={() => setSupportModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faHeadset} className="me-2" />
            Contact Support
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Use your support details here.</p>
          <p className="mb-0 text-muted">
            Example: support@yourcompany.com | +91-XXXXXXXXXX
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setSupportModalOpen(false)}>
            Close
          </Button>
          {/* <Button
            className="act-btn"
            as={Link}
            to={getSupportPath()}
            onClick={() => setSupportModalOpen(false)}
          >
            Open Support
          </Button> */}
        </Modal.Footer>
      </Modal>
    </main>
  );
}